import {use, useCallback, useMemo, useRef} from 'react'
import {Comments} from '../comments/Comments'
import {CommentsContext} from '../comments/CommentsContext'
import {CommentsViewControls} from '../comments/CommentsViewControls'
import type {IssueCommentComposerHandle} from '../comments/IssueCommentComposer'
import {useFilteredCommentsData} from '../comments/use-filtered-comments-data'
import {useLayoutAuthorLogin} from '../../stores/layout-selectors'
import styles from './CommentsSection.module.css'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useCommentsFilterState} from '../comments/use-comments-filter-state'
import {Blankslate} from '@primer/react/experimental'
import {AlertIcon} from '@primer/octicons-react'
import {Link, VisuallyHidden} from '@primer/react'
import {useViewModeFromSearchParams} from './data/use-comments-routing'
import {useCommentsSortState} from '../comments/use-comments-sort-state'
import {OVERVIEW_COMMENTS_ANCHOR_ID} from '../../helpers/constants'
import {PullRequestCommentStateAction} from './PullRequestCommentStateAction'
import {useConversationCommentsLiveUpdates} from '../../hooks/use-conversation-comments-live-updates'

interface CommentsSectionProps {
  skipTargetId?: string
}

function CommentsHeading({skipTargetId}: CommentsSectionProps) {
  return (
    <div className={styles.headingArea}>
      <div className={styles.headingWithSkipLink}>
        <h2 className={styles.heading}>Comments</h2>
        {skipTargetId && (
          <VisuallyHidden className={styles.skipLinkContainer}>
            <Link href={`#${skipTargetId}`} className={styles.skipLink}>
              Skip comments
            </Link>
          </VisuallyHidden>
        )}
      </div>
    </div>
  )
}

function ErrorFallback() {
  return (
    <Blankslate>
      <Blankslate.Visual>
        <AlertIcon size="medium" />
      </Blankslate.Visual>
      <Blankslate.Heading>Failed to load comments</Blankslate.Heading>
      <Blankslate.Description>
        Please{' '}
        <Link inline href="#" onClick={() => window.location.reload()}>
          refresh the page
        </Link>{' '}
        to try again.
      </Blankslate.Description>
    </Blankslate>
  )
}

export function CommentsSection({skipTargetId}: CommentsSectionProps) {
  const [viewMode, setViewMode] = useViewModeFromSearchParams()

  const commentsContextValue = useMemo(() => ({viewMode, setViewMode}), [viewMode, setViewMode])

  return (
    <section id={OVERVIEW_COMMENTS_ANCHOR_ID} className={styles.container} tabIndex={-1}>
      <ErrorBoundary
        fallback={
          <>
            <CommentsHeading skipTargetId={skipTargetId} />
            <ErrorFallback />
          </>
        }
        boundaryName="OverviewCommentsSection"
      >
        <CommentsContext value={commentsContextValue}>
          <CommentsSectionContent skipTargetId={skipTargetId} />
        </CommentsContext>
      </ErrorBoundary>
    </section>
  )
}

function CommentsSectionContent({skipTargetId}: CommentsSectionProps) {
  const pullRequestAuthorLogin = useLayoutAuthorLogin()

  useConversationCommentsLiveUpdates()

  const viewMode = use(CommentsContext).viewMode
  const {filterState, setCurrentFilterState, resetFilterStates} = useCommentsFilterState(viewMode)
  const [sort, setSort] = useCommentsSortState(viewMode)
  const {authorList, filteredConversationItems, totalConversationItemsCount, isPending, isError} =
    useFilteredCommentsData({filterState, sort})

  const composerRef = useRef<IssueCommentComposerHandle>(null)
  const handleQuoteReply = useCallback(
    (quotedText: string) => composerRef.current?.insertQuote(quotedText, {append: true}),
    [],
  )

  return (
    <>
      <div className={styles.controlsRow}>
        <CommentsHeading skipTargetId={skipTargetId} />
        <CommentsViewControls
          authorList={authorList}
          filterState={filterState}
          setFilterState={setCurrentFilterState}
          sort={sort}
          setSort={setSort}
          viewMode={viewMode}
        />
      </div>
      <Comments
        filteredConversationItems={filteredConversationItems}
        sort={sort}
        handleQuoteReply={handleQuoteReply}
        pullRequestAuthorLogin={pullRequestAuthorLogin}
        totalConversationItemsCount={totalConversationItemsCount}
        composerRef={composerRef}
        renderAdditionalComposerActions={context => <PullRequestCommentStateAction {...context} />}
        onSubmitted={resetFilterStates}
        isPending={isPending}
        isError={isError}
      />
    </>
  )
}
